import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { LoginService } from '../login.service';
import { trigger, transition, style, animate, state } from '@angular/animations';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  animations: [
    trigger('stepAnimation', [
      // State for when the element is visible
      state('in', style({ opacity: 1, transform: 'translateY(0)' })),
      // Fade-in transition when entering the DOM
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('300ms ease-out'),
      ]),
      // Fade-out transition when leaving the DOM
      transition(':leave', [
        animate('100ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' })),
      ]),
    ]),
    trigger('heightAnimation', [
      // Define a state for each step based on its expected content height
      state('initial', style({ height: '150px' })),
      state('verify', style({ height: '320px' })),
      state('success', style({ height: '120px' })),
      // Animate the height change between states
      transition('* <=> *', [animate('400ms ease-in-out')]),
    ]),
  ],
  standalone: false,
})
export class ForgotPasswordComponent {
  step: 'initial' | 'verify' | 'success' = 'initial';
  errorMessage: string = '';
  isLoading: boolean = false;

  // Form fields
  username: string = '';
  code: string = '';
  newPassword: string = '';
  confirmPassword: string = '';
  hidePassword = true;
  hideConfirmPassword = true;

  constructor(
    public dialogRef: MatDialogRef<ForgotPasswordComponent>,
    public ls: LoginService
  ) {}

  requestPasswordReset() {
    if (!this.username) {
      this.errorMessage = 'Please enter your username.';
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';

    this.ls.sendPasswordResetCode(this.username).subscribe({
      next: (response) => {
        if (!response.success) {
          this.errorMessage =
            response?.message || 'An unexpected error occurred. Please try again.';
          this.isLoading = false;
        } else {
          this.step = 'verify'; // Move to the next step
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error('Error sending reset code', err);
        this.errorMessage = err.error?.error || 'An unexpected error occurred. Please try again.';
        this.isLoading = false;
      },
    });
  }
  private validatePassword(password: string): { isValid: boolean; message: string } {
    if (password.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters long.' };
    }
    if (!/[A-Z]/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one capital letter.' };
    }
    if (!/[!@#$%]/.test(password)) {
      return {
        isValid: false,
        message: 'Password must contain one of the following special characters: !@#$%',
      };
    }
    return { isValid: true, message: '' };
  }

  submitNewPassword() {
    if (!this.code || !this.newPassword || !this.confirmPassword) {
      this.errorMessage = 'Please fill in all fields.';
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.errorMessage = 'Passwords do not match.';
      return;
    }
    // PW has to be 8 Char,1 capital and 1 of !@#$% keys..
    const passwordValidation = this.validatePassword(this.newPassword);
    if (!passwordValidation.isValid) {
      this.errorMessage = passwordValidation.message;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.ls.resetPassword(this.username, this.code, this.newPassword).subscribe({
      next: (response) => {
        if (!response.success) {
          this.errorMessage =
            response?.message || 'An unexpected error occurred. Please try again.';
          this.isLoading = false;
        } else {
          this.step = 'success';
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error('Error resetting password', err);
        this.errorMessage = err.error?.error || 'An unexpected error occurred. Please try again.';
        this.isLoading = false;
      },
    });
  }

  closeModal() {
    this.dialogRef.close();
  }
}
